import axios from "axios";
import { apiURL } from "./constants";

const API_BASE = `${apiURL}/auth`;

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('access_token')}`;
  try {
    const response = await axios({
      url: `${API_BASE}${endpoint}`,
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      data: options.data,
      withCredentials: true,
    });
    return response;
  } catch (error) {
    console.error('There was an error!', error);
    throw error;
  }
};

// ==================== PERMISSIONS API ====================

/**
 * List permissions with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Paginated response with permissions
 */
export const getPermissions = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.app_label) queryParams.append('app_label', params.app_label);
  if (params.resource) queryParams.append('resource', params.resource);
  if (params.action) queryParams.append('action', params.action);
  if (params.search) queryParams.append('search', params.search);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.pagination !== undefined) queryParams.append('pagination', params.pagination);
  if (params.group_by_app !== undefined) queryParams.append('group_by_app', params.group_by_app);

  const queryString = queryParams.toString();
  return apiRequest(`/permissions/${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a single permission by ID
 * @param {number} id - Permission ID
 * @returns {Promise<Object>} Permission object
 */
export const getPermission = async (id) => {
  return apiRequest(`/permissions/${id}/`);
};

/**
 * Create a new permission
 * @param {Object} data - Permission data
 * @returns {Promise<Object>} Created permission
 */
export const createPermission = async (data) => {
  return apiRequest('/permissions/', {
    method: 'POST',
    data: data,
  });
};

/**
 * Update a permission
 * Backend rejects any body that includes `codename` (immutable after create); `id` is path-only.
 * @param {number} id - Permission ID
 * @param {Object} data - Updated permission data
 * @param {boolean} partial - Use PATCH if true, PUT if false
 * @returns {Promise<Object>} Updated permission
 */
export const updatePermission = async (id, data, partial = true) => {
  const payload = { ...(data || {}) };
  delete payload.codename;
  delete payload.id;
  return apiRequest(`/permissions/${id}/`, {
    method: partial ? 'PATCH' : 'PUT',
    data: payload,
  });
};

/**
 * Delete a permission
 * @param {number} id - Permission ID
 * @returns {Promise<void>}
 */
export const deletePermission = async (id) => {
  return apiRequest(`/permissions/${id}/`, {
    method: 'DELETE',
  });
};

// ==================== ROLES API ====================

/**
 * List roles with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Paginated response with roles
 */
export const getRoles = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.is_system_role !== undefined) queryParams.append('is_system_role', params.is_system_role);
  if (params.branch) queryParams.append('branch', params.branch);
  if (params.search) queryParams.append('search', params.search);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.pagination !== undefined) queryParams.append('pagination', params.pagination);

  const queryString = queryParams.toString();
  return apiRequest(`/roles/${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a single role by ID
 * @param {number} id - Role ID
 * @returns {Promise<Object>} Role object
 */
export const getRole = async (id) => {
  return apiRequest(`/roles/${id}/`);
};

/**
 * Create a new role
 * @param {Object} data - Role data
 * @returns {Promise<Object>} Created role
 */
export const createRole = async (data) => {
  return apiRequest('/roles/', {
    method: 'POST',
    data: data,
  });
};

/**
 * Update a role
 * @param {number} id - Role ID
 * @param {Object} data - Updated role data
 * @param {boolean} partial - Use PATCH if true, PUT if false
 * @returns {Promise<Object>} Updated role
 */
export const updateRole = async (id, data, partial = true) => {
  return apiRequest(`/roles/${id}/`, {
    method: partial ? 'PATCH' : 'PUT',
    data: data,
  });
};

/**
 * Delete a role
 * @param {number} id - Role ID
 * @returns {Promise<void>}
 */
export const deleteRole = async (id) => {
  return apiRequest(`/roles/${id}/`, {
    method: 'DELETE',
  });
};

/**
 * Assign permissions to a role
 * @param {number} roleId - Role ID
 * @param {Array<number>} permissionIds - Array of permission IDs
 * @returns {Promise<Object>} Updated role
 */
export const assignPermissionsToRole = async (roleId, permissionIds) => {
  return apiRequest(`/roles/${roleId}/assign_permissions/`, {
    method: 'POST',
    data: { permission_ids: permissionIds },
  });
};

/**
 * Get permissions for a role
 * @param {number} roleId - Role ID
 * @returns {Promise<Array>} Array of permissions
 */
export const getRolePermissions = async (roleId) => {
  return apiRequest(`/roles/${roleId}/permissions/`);
};

// ==================== USERS API ====================

/**
 * List users with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Paginated response with users
 */
export const getUsers = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.branch) queryParams.append('branch', params.branch);
  if (params.deleted !== undefined) queryParams.append('deleted', params.deleted);
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.is_staff !== undefined) queryParams.append('is_staff', params.is_staff);
  if (params.is_superuser !== undefined) queryParams.append('is_superuser', params.is_superuser);
  if (params.search) queryParams.append('search', params.search);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.pagination !== undefined) queryParams.append('pagination', params.pagination);

  const queryString = queryParams.toString();
  return apiRequest(`/users/${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a single user by ID
 * @param {number} id - User ID
 * @returns {Promise<Object>} User object
 */
export const getUser = async (id) => {
  return apiRequest(`/users/${id}/`);
};

/**
 * Create a new user
 * @param {Object} data - User data
 * @returns {Promise<Object>} Created user
 */
export const createUser = async (data) => {
  return apiRequest('/users/', {
    method: 'POST',
    data: data,
  });
};

/**
 * Update a user
 * @param {number} id - User ID
 * @param {Object} data - Updated user data
 * @param {boolean} partial - Use PATCH if true, PUT if false
 * @returns {Promise<Object>} Updated user
 */
export const updateUser = async (id, data, partial = true) => {
  return apiRequest(`/users/${id}/`, {
    method: partial ? 'PATCH' : 'PUT',
    data: data,
  });
};

/**
 * Delete a user
 * @param {number} id - User ID
 * @returns {Promise<void>}
 */
export const deleteUser = async (id) => {
  return apiRequest(`/users/${id}/`, {
    method: 'DELETE',
  });
};

// ==================== USER-ROLES API ====================

/**
 * List user-role assignments with optional filters
 * @param {Object} params - Query parameters
 * @returns {Promise<Object>} Paginated response with user-role assignments
 */
export const getUserRoles = async (params = {}) => {
  const queryParams = new URLSearchParams();
  
  if (params.user) queryParams.append('user', params.user);
  if (params.role) queryParams.append('role', params.role);
  if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);
  if (params.branch) queryParams.append('branch', params.branch);
  if (params.assigned_by) queryParams.append('assigned_by', params.assigned_by);
  if (params.search) queryParams.append('search', params.search);
  if (params.ordering) queryParams.append('ordering', params.ordering);
  if (params.page) queryParams.append('page', params.page);
  if (params.page_size) queryParams.append('page_size', params.page_size);
  if (params.pagination !== undefined) queryParams.append('pagination', params.pagination);

  const queryString = queryParams.toString();
  return apiRequest(`/user-roles/${queryString ? `?${queryString}` : ''}`);
};

/**
 * Get a single user-role assignment by ID
 * @param {number} id - User-role assignment ID
 * @returns {Promise<Object>} User-role assignment object
 */
export const getUserRole = async (id) => {
  return apiRequest(`/user-roles/${id}/`);
};

/**
 * Create a new user-role assignment
 * @param {Object} data - User-role assignment data
 * @returns {Promise<Object>} Created user-role assignment
 */
export const createUserRole = async (data) => {
  return apiRequest('/user-roles/', {
    method: 'POST',
    data: data,
  });
};

/**
 * Update a user-role assignment
 * @param {number} id - User-role assignment ID
 * @param {Object} data - Updated user-role assignment data
 * @param {boolean} partial - Use PATCH if true, PUT if false
 * @returns {Promise<Object>} Updated user-role assignment
 */
export const updateUserRole = async (id, data, partial = true) => {
  return apiRequest(`/user-roles/${id}/`, {
    method: partial ? 'PATCH' : 'PUT',
    data: data,
  });
};

/**
 * Delete a user-role assignment
 * @param {number} id - User-role assignment ID
 * @returns {Promise<void>}
 */
export const deleteUserRole = async (id) => {
  return apiRequest(`/user-roles/${id}/`, {
    method: 'DELETE',
  });
};

/**
 * TODO(permissions #23): No UI consumer today.
 * Permission: PERMISSIONS.USER_ROLE_ASSIGN.
 * When wiring up, gate the caller via <PermissionGate /> or hasPermission().
 * See ENDPOINTS_USAGE.md Phase 7.
 */
/**
 * Assign role to user (legacy endpoint)
 * @param {Object} data - Assignment data { user_id, role_id, branch_id?, expires_at? }
 * @returns {Promise<Object>} Created assignment
 */
export const assignRoleToUser = async (data) => {
  return apiRequest('/user-roles/assign_role_to_user/', {
    method: 'POST',
    data: data,
  });
};

/**
 * TODO(permissions #24): No UI consumer today.
 * Permission: PERMISSIONS.USER_ROLE_REVOKE.
 * When wiring up, gate the caller via <PermissionGate /> or hasPermission().
 * See ENDPOINTS_USAGE.md Phase 7.
 */
/**
 * Revoke role from user
 * @param {number} id - User-role assignment ID
 * @returns {Promise<Object>} Updated assignment
 */
export const revokeRoleFromUser = async (id) => {
  return apiRequest(`/user-roles/${id}/revoke_role/`, {
    method: 'POST',
  });
};


